import React, {useState, useEffect} from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Button,
    useTheme,
    useMediaQuery,
    Paper,
    Stack,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HomeIcon from '@mui/icons-material/Home';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import {Chip} from '@mui/material';
import KKMPopup from './KKMPopup';
import PlusIcon from '../../Icons/PlusIcon';
import TradePlacesPopup from './TradePlacesPopup';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import HelpPopover from '../../../ui/HelpPopover';
import ServiceRentIcon from '../../../ui/ServiceRentIcon';
import StatusIcon from '../../../ui/StatusIcon';
import EditMerchantPopup from './EditMerchantPopup';
import ResetMerchantPasswordPopup from './ResetMerchantPasswordPopup';
import {getTextColorByPaymentInfo} from '../../../functionts/getTextColorByPaymentInfo';

interface KKMTableProps {
    data: any[];
    serviceCenterId: number | null;
    filters: {
        merchantSearch: string;
        kkmSearch: string;
    };
    isLoading: boolean;
    onKkmUpdate: (merchantId: number, updatedKkm: any) => void;
    onKkmCreate: (merchantId: number, newKkm: any) => void;
    onKkmDelete: (merchantId: number, deletedKkmId: number) => void;
    onMerchantUpdate?: (updatedMerchant: any) => void;
}

const KKMTable = ({data, serviceCenterId, filters, isLoading, onKkmUpdate, onKkmCreate, onKkmDelete, onMerchantUpdate}: KKMTableProps) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [activeRow, setActiveRow] = useState<any | null>(null);
    const [isCreate, setIsNew] = useState(true);
    const [openTradePlaces, setOpenTradePlaces] = useState(false);
    const [merchantIdForTradePlaces, setMerchantIdForTradePlaces] = useState<number | null>(null);
    const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>(() => {
        const saved = localStorage.getItem('kkmAccordionsState');
        return saved ? JSON.parse(saved) : {};
    });
    const [editingMerchantId, setEditingMerchantId] = useState<number | null>(null);
    const [resetMerchantId, setResetMerchantId] = useState<number | null>(null);

    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.down('lg'));

    useEffect(() => {
        localStorage.setItem('kkmAccordionsState', JSON.stringify(expandedAccordions));
    }, [expandedAccordions]);

    useEffect(() => {
        if (filters.kkmSearch) {
            const newExpanded: Record<string, boolean> = {};

            filteredData.forEach(merchant => {
                // Разворачиваем аккордеон, если у мерчанта есть подходящие кассы
                if (merchant.kkm_list && merchant.kkm_list.length > 0) {
                    newExpanded[merchant.id] = true;
                }
            });

            setExpandedAccordions(prev => ({
                ...prev,
                ...newExpanded,
            }));
        }
    }, [filters.kkmSearch]);

    const handleAccordionChange = (merchantId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordions(prev => ({
            ...prev,
            [merchantId]: isExpanded,
        }));
    };

    const normalized = (str: string) => str?.toLowerCase().trim();

    const filterMerchants = (merchant: any) => {
        const q = normalized(filters.merchantSearch);
        return !q || normalized(merchant.name).includes(q) || normalized(merchant.inn).includes(q);
    };

    const filterKKMs = (kkm: any) => {
        const q = normalized(filters.kkmSearch);
        return (
            !q ||
            normalized(kkm.serial_number).includes(q) ||
            normalized(kkm.reg_number).includes(q) ||
            normalized(kkm.trade_place_name || '').includes(q)
        );
    };

    const filteredData = data
        .filter(filterMerchants)
        .map(merchant => ({
            ...merchant,
            kkm_list: (merchant.kkm_list || []).filter(filterKKMs),
        }))
        .filter(merchant => !filters.kkmSearch || (merchant.kkm_list && merchant.kkm_list.length > 0));

    const copyInfo = (e: React.MouseEvent, info: string) => {
        void navigator.clipboard.writeText(info);
        e.preventDefault();
        e.stopPropagation();
    };

    const openPopupNew = (e: React.MouseEvent, merchant: any) => {
        setIsNew(true);
        setActiveRow({user_id: merchant.id, merchant_name: merchant.name});
        setOpenPopup(true);
        e.preventDefault();
        e.stopPropagation();
    };

    const openPopupEdit = (e: React.MouseEvent, merchant: any, kkm: any) => {
        setIsNew(false);
        setActiveRow({user_id: merchant.id, merchant_name: merchant.name, ...kkm});
        setOpenPopup(true);
        e.preventDefault();
        e.stopPropagation();
    };

    const handleOpenTradePlaces = (e: React.MouseEvent, id: number) => {
        setMerchantIdForTradePlaces(id);
        setOpenTradePlaces(true);
        e.preventDefault();
        e.stopPropagation();
    };

    // Для того, чтобы развернуть таблицу (акардион) с кассами при создании новой, - чтобы было её видно
    const handleKkmCreate = (merchantId: number, newKkm: any) => {
        onKkmCreate(merchantId, newKkm);

        setExpandedAccordions(prev => ({
            ...prev,
            [merchantId]: true,
        }));
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" sx={{height: '400px', paddingTop: '200px'}}>
                <CircularProgress />
            </Box>
        );
    }

    if (!filteredData.length) {
        return (
            <Box justifyContent="center" textAlign="center" sx={{height: '400px', paddingTop: '200px'}}>
                <Typography variant="body1" color="textSecondary">
                    Ничего не найдено по заданным фильтрам
                </Typography>
            </Box>
        );
    }

    const firstMerchantId = data[0]?.id;

    const getTextColorByPaymentInfo = (statusColor: string | null) => {
        let textColor = 'textSecondary';

        if (statusColor === 'green') {
            textColor = 'success.main';
        } else if (statusColor === 'yellow') {
            textColor = 'warning.main';
        } else if (statusColor === 'red') {
            textColor = 'error.main';
        }

        return textColor;
    };

    return (
        <Box>
            {filteredData.map((merchant, index) => (
                <Accordion
                    key={merchant.id}
                    expanded={expandedAccordions[merchant.id] ?? false}
                    onChange={handleAccordionChange(merchant.id)}
                    sx={{
                        mb: 1,
                        '&:hover .merchant-hover-action': {visibility: 'visible'},
                        backgroundColor: merchant._justAdded ? 'rgba(255, 255, 0, 0.15)' : 'inherit',
                        transition: 'background-color 0.5s ease',
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                            display="flex"
                            alignItems={isLg ? 'flex-start' : 'center'}
                            justifyContent="space-between"
                            width="100%"
                            flexDirection={isLg ? 'column' : 'row'}
                            gap={isLg ? 1 : 0}
                        >
                            <Box>
                                <Typography variant="subtitle1" color={merchant.kkm_list?.length ? 'textPrimary' : 'textSecondary'}>
                                    {merchant.id === firstMerchantId && (
                                        <Tooltip title="Моя компания (ЦТО)">
                                            <HomeIcon
                                                fontSize="small"
                                                sx={{
                                                    color: 'success.main',
                                                    mr: 0.5,
                                                    mt: -0.5,
                                                    verticalAlign: 'middle',
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    <strong>{merchant.name}</strong>
                                    {merchant?.flag_vat === 'Y' ? (
                                        <Tooltip title="Является плательщиком НДС">
                                            <Chip
                                                label="НДС"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    padding: 0,
                                                    minWidth: 'unset',
                                                    backgroundColor: '#f0f0f0',
                                                    color: '#8F96A6',
                                                    borderRadius: '4px',
                                                    marginLeft: isLg ? 0 : '15px',
                                                    marginTop: isLg ? '6px' : '-3px',
                                                }}
                                                size="small"
                                            />
                                        </Tooltip>
                                    ) : null}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center'}}
                                    onClick={e => copyInfo(e, merchant.inn)}
                                >
                                    ИИН/БИН: {merchant.inn}
                                    <Tooltip title="Скопировать ИИН/БИН">
                                        <ContentCopyIcon fontSize="inherit" sx={{ml: 0.5}} />
                                    </Tooltip>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {merchant.phone ? 'Тел.: ' + merchant.phone : ''}
                                    {merchant.phone && merchant.e_mail ? ' | ' : ''}
                                    {merchant.e_mail ? 'Email: ' + merchant.e_mail : ''}
                                </Typography>
                            </Box>

                            <Box display="flex" gap={1} width={isLg ? '100%' : 'auto'} justifyContent={isLg ? 'space-between' : 'flex-end'}>
                                <Box
                                    className="merchant-hover-action"
                                    sx={{visibility: isLg ? 'shown' : 'hidden', display: 'flex', gap: 1, alignItems: 'center'}}
                                >
                                    <Tooltip title="Cброс пароля мерчанта">
                                        <IconButton
                                            onClick={e => {
                                                e.stopPropagation();
                                                setResetMerchantId(merchant.id);
                                            }}
                                            sx={{color: 'text.secondary'}}
                                            size={isLg ? 'small' : 'medium'}
                                        >
                                            <LockResetIcon fontSize={isLg ? 'small' : 'inherit'} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Редактировать данные мерчанта">
                                        <IconButton
                                            onClick={e => {
                                                e.stopPropagation();
                                                setEditingMerchantId(merchant.id);
                                            }}
                                            sx={{color: 'text.secondary'}}
                                            size={isLg ? 'small' : 'medium'}
                                        >
                                            <EditIcon fontSize={isLg ? 'small' : 'inherit'} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Редактировать справочник «Места торговли»">
                                        <IconButton
                                            onClick={e => void handleOpenTradePlaces(e, merchant.id)}
                                            sx={{color: 'text.secondary'}}
                                            size={isLg ? 'small' : 'medium'}
                                        >
                                            <EditLocationAltIcon fontSize={isLg ? 'small' : 'inherit'} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Button size="small" variant="outlined" onClick={e => void openPopupNew(e, merchant)} startIcon={<PlusIcon />}>
                                    Добавить ККМ
                                </Button>
                            </Box>
                        </Box>
                    </AccordionSummary>

                    {merchant.kkm_list?.length ? (
                        <AccordionDetails>
                            {isLg ? (
                                <Stack spacing={1}>
                                    {(merchant.kkm_list || []).map((kkm: any) => (
                                        <Paper key={kkm.kkm_id} variant="outlined" sx={{p: 1}}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                <Box display="flex" flexDirection="column" gap={0.5}>
                                                    <Box display="flex" alignItems="center" gap={1} mb={isLg ? 1 : 0}>
                                                        <StatusIcon kkm={kkm} />
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center'}}
                                                            onClick={e => copyInfo(e, kkm.serial_number)}
                                                        >
                                                            {kkm.serial_number || '-'}
                                                            <Tooltip title="Скопировать серийный номер">
                                                                <ContentCopyIcon fontSize="inherit" sx={{ml: 0.5}} />
                                                            </Tooltip>
                                                        </Typography>
                                                    </Box>

                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        Рег. номер: {kkm.reg_number || '-'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        Модель: {kkm.model}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        ОФД: {kkm.ofd?.name || '-'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        {kkm.department?.name || ''}
                                                        {kkm.department?.name && kkm.department?.address ? ' / ' : ''}
                                                        {kkm.department?.address || ''}
                                                    </Typography>

                                                    <Box>
                                                        <ServiceRentIcon kkm={kkm} />
                                                    </Box>
                                                </Box>

                                                <Box textAlign="right" minWidth={75}>
                                                    <Typography
                                                        variant="body2"
                                                        color={getTextColorByPaymentInfo(kkm.create_payment_info?.color_status)}
                                                    >
                                                        {kkm.create_payment_info?.balance_control_disabled ||
                                                        (kkm.create_payment_info?.payment_amount_by_rent === 0 &&
                                                            !kkm.create_payment_info?.upcoming_payment_amount)
                                                            ? 0
                                                            : kkm.balance_value}
                                                    </Typography>

                                                    {kkm.create_payment_info?.is_new_kkm_created_before_cutoff &&
                                                        !kkm.create_payment_info?.balance_control_disabled &&
                                                        kkm.create_payment_info?.payment_amount_by_rent > 0 && (
                                                            <HelpPopover
                                                                customIcon={<CardGiftcardOutlinedIcon fontSize="small" color="disabled" />}
                                                                content={
                                                                    <>
                                                                        Кассы, добавленные в первые дни месяца, работают бесплатно до 5 числа
                                                                        этого месяца (включительно).
                                                                        <br />
                                                                        <br />
                                                                        Оплатите до этого срока, чтобы кассы продолжили работать без перебоев.
                                                                    </>
                                                                }
                                                            />
                                                        )}

                                                    <Typography
                                                        variant="caption"
                                                        color={getTextColorByPaymentInfo(kkm.create_payment_info?.color_status)}
                                                        display="block"
                                                        mt={0.5}
                                                    >
                                                        {kkm.create_payment_info?.paid_until_date || ''}
                                                    </Typography>

                                                    <Box mt={1} display="flex" justifyContent="flex-end">
                                                        <IconButton onClick={e => void openPopupEdit(e, merchant, kkm)} size="small">
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Статус</TableCell>
                                            <TableCell>Модель</TableCell>
                                            <TableCell>Рег.&nbsp;номер</TableCell>
                                            <TableCell>Серийный&nbsp;номер</TableCell>
                                            <TableCell>ОФД</TableCell>
                                            <TableCell>Место&nbsp;торговли</TableCell>
                                            <TableCell>Тарифы</TableCell>
                                            <TableCell>Баланс</TableCell>
                                            <TableCell style={{minWidth: '120px'}}>
                                                Оплачено&nbsp;до
                                                <HelpPopover
                                                    content={
                                                        <>
                                                            Дата, по которую включительно оплачена услуга.
                                                            <br />
                                                            <br />
                                                            Прочерк – у касс без платного тарифа.
                                                        </>
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>&nbsp;</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(merchant.kkm_list || []).map((kkm: any) => (
                                            <TableRow
                                                key={kkm.kkm_id}
                                                sx={{
                                                    backgroundColor: kkm._justAdded ? 'rgba(255, 255, 0, 0.15)' : 'inherit',
                                                    transition: 'background-color 0.5s ease',
                                                    '&:last-child td, &:last-child th': {borderBottom: 0},
                                                    '&:hover .edit-icon': {visibility: 'visible'},
                                                }}
                                            >
                                                <TableCell>
                                                    <StatusIcon kkm={kkm} />
                                                </TableCell>
                                                <TableCell>{kkm.model}</TableCell>
                                                <TableCell>{kkm.reg_number}</TableCell>
                                                <TableCell>{kkm.serial_number}</TableCell>
                                                <TableCell>{kkm.ofd?.name}</TableCell>
                                                <TableCell>
                                                    {kkm.department?.name || ''}
                                                    {kkm.department?.name && kkm.department?.address ? ' / ' : ''}
                                                    {kkm.department?.address || ''}
                                                </TableCell>
                                                <TableCell>
                                                    <ServiceRentIcon kkm={kkm} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                        <Typography
                                                            variant="body2"
                                                            color={getTextColorByPaymentInfo(kkm.create_payment_info?.color_status)}
                                                        >
                                                            {kkm.create_payment_info?.balance_control_disabled ||
                                                            (kkm.create_payment_info?.payment_amount_by_rent === 0 &&
                                                                !kkm.create_payment_info?.upcoming_payment_amount)
                                                                ? 0
                                                                : kkm.balance_value}
                                                        </Typography>

                                                        {kkm.create_payment_info?.is_new_kkm_created_before_cutoff &&
                                                            !kkm.create_payment_info?.balance_control_disabled &&
                                                            kkm.create_payment_info?.payment_amount_by_rent > 0 && (
                                                                <HelpPopover
                                                                    customIcon={<CardGiftcardOutlinedIcon fontSize="small" color="disabled" />}
                                                                    content={
                                                                        <>
                                                                            Кассы, добавленные в первые дни месяца, работают бесплатно до 5
                                                                            числа этого месяца (включительно).
                                                                            <br />
                                                                            <br />
                                                                            Оплатите до этого срока, чтобы кассы продолжили работать без
                                                                            перебоев.
                                                                        </>
                                                                    }
                                                                />
                                                            )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        color={getTextColorByPaymentInfo(kkm.create_payment_info?.color_status)}
                                                    >
                                                        {kkm.create_payment_info?.paid_until_date}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box className="edit-icon" sx={{visibility: 'hidden'}}>
                                                        <Tooltip title="Редактировать данные ККМ">
                                                            <IconButton onClick={e => void openPopupEdit(e, merchant, kkm)} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </AccordionDetails>
                    ) : null}
                </Accordion>
            ))}

            <KKMPopup
                deskOpen={openPopup}
                setOpenDesk={setOpenPopup}
                isCreate={isCreate}
                activeRow={activeRow}
                userId={activeRow?.user_id ?? null}
                serviceCenterId={serviceCenterId}
                isLoading={isLoading}
                onKkmUpdate={onKkmUpdate}
                onKkmCreate={handleKkmCreate}
                onKkmDelete={onKkmDelete}
            />

            <TradePlacesPopup open={openTradePlaces} onClose={() => setOpenTradePlaces(false)} userId={merchantIdForTradePlaces} />

            <EditMerchantPopup
                open={!!editingMerchantId}
                userId={editingMerchantId}
                serviceCenterId={serviceCenterId}
                onClose={() => setEditingMerchantId(null)}
                onSuccess={updatedMerchant => {
                    onMerchantUpdate?.(updatedMerchant);
                }}
            />

            <ResetMerchantPasswordPopup
                open={!!resetMerchantId}
                userId={resetMerchantId}
                serviceCenterId={serviceCenterId}
                onClose={() => setResetMerchantId(null)}
                onSuccess={() => {
                    // Необязательное действие после успешного сброса пароля
                }}
            />
        </Box>
    );
};

export default KKMTable;
